import React, { useEffect, useState, useRef } from 'react';
import {
  Controller,
  ControllerRenderProps,
  FieldValues,
  useForm,
} from 'react-hook-form';
import styles from './index.module.scss';
import StockViewer from './components/stockpopup';
import { StockItem } from './components/stockItem';
import { getAllModels } from '~/pages/compare-cars/services';
import { CompareModel, CompareVariant } from '~/pages/compare-cars/models';
import {
  Autocomplete,
  Loading,
  TooltipDisclaimer,
} from '~/common/components/ui-elements';
import { Option } from '~/common/models';
import AsyncSelect from 'react-select/async';
import ReCAPTCHA from 'react-google-recaptcha';
import RYITankyouMessage from './components/thankyou';
import { dataLayerPushRYIForm } from './datalayer/push-ryi-form';
import { digestStringSHA256 } from '~/common/utils/hash';

interface RegisterYourInterestV2Prop {
  competitionTitle: string;
  competitionSubTitle: string;
  competitionTerms: string;
  competitionSubmitSuccessText: string;
  privacyPolicy: string;

  // Form field Controls
  displayFirstName: boolean;
  displayLastName: boolean;
  displayPhone: boolean;
  displayEmail: boolean;
  displayPostCode: boolean;
  displayPreOrderingInterestField: boolean;
  displayVariantType: boolean;
  displayModelTestDriven: boolean;
  displayDealerTextField: boolean;
  displayDateOfBirth: boolean;
  displayStockAvailabilityMatrix: boolean;
  displayConsentStatement: boolean;
  displayPrivacyCollectionStatement: boolean;
  displayRetailerCode: boolean;
  variantType: string;
  displayGender: boolean;
  modelName: string;
  gaFormName: string;
  competitionType: string;
  salesforceLeads: string;
  salesforceDepartment: string;
  salesforceLeadForm: string;
  salesforceLeadTemperature: string;

  reCaptchaKey: string;
}

type PostCodeOption = Option & {
  name: string;
  state: string;
};
export default (props: RegisterYourInterestV2Prop) => {
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const [showModal, setShowModal] = useState(false);
  const [selectedModel, setSelectedModel] = useState<StockItem>();
  const [vehicleModels, setVehicleModels] = useState<CompareModel[]>();
  const [defaultOptions, setDefaultOptions] = useState<PostCodeOption[]>([]);
  const [suburb, setSuburb] = useState<PostCodeOption | undefined>(undefined);
  const autocompleteRef = React.useRef<AsyncSelect<Option> | null>(null);
  const [matchCode, setMatchCode] = useState('');
  const [userInterestedInPreorder, setInterestedInPreorder] = useState('No');
  const [selectedVariant, setSelectedVariant] = useState('');
  const [captchaError, setCaptchaError] = useState(false);
  const [displayLoading, setDisplayLoading] = useState(false);
  const [displayThankyou, setDisplayThankyou] = useState(false);
  const [vehicleVariants, setVehicleVariants] = useState<CompareVariant[]>();
  const [disabledMatrix, setDisabledMatrix] = useState(true);
  const [displayPostcodeError, setDisplayPostcodeError] = useState(false);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const handleCaptchaChange = (token: string | null) => {
    console.log('Captcha token:', token);
    // Send token to your backend for verification
  };

  const onSubmit = async (formData: any) => {
    const prelaunchModel = {
      ...formData,
      postcode: suburb?.value ?? '',
      displayPreOrderingInterestField: props.displayPreOrderingInterestField,
      displayDealerTextField: props.displayDealerTextField,
      displayDateOfBirth: props.displayDateOfBirth,
      displayRetailerCode: props.displayRetailerCode,
      displayStockAvailabilityMatrix: props.displayStockAvailabilityMatrix,
      displayVariantType: props.displayVariantType,
      varianttype: props.variantType,
      displayModelTestDriven: props.displayModelTestDriven,
      displayConsentStatement: props.displayConsentStatement,
      displayPrivacyCollectionStatement:
        props.displayPrivacyCollectionStatement,
      modelName: props.modelName,
      gaFormName: props.gaFormName,
      competitionType: props.competitionType,
      salesforceLeads: props.salesforceLeads,
      salesforceDepartment: props.salesforceDepartment,
      salesforceLeadForm: props.salesforceLeadForm,
      salesforceLeadTemperature: props.salesforceLeadTemperature,
      interestedModelColour: selectedModel?.exteriorColourDescription,
      interestedVariant:
        selectedVariant === '' ? selectedModel?.displayName : selectedVariant,
      interestedModel: props.modelName,
      interestedInPreorder: userInterestedInPreorder,
      dateOfBirth: formData?.dateOfBirth ?? '',
      dealer: formData?.dealer ?? '',
      phone: formData?.phone ?? '',
      retailerCode: formData?.retailerCode ?? '',
      displayGender: props.displayGender,
      genderType: formData?.gender ?? '',
    };
    console.log('loggingPrelaunch', prelaunchModel);

    // submit to API const recaptchaToken = await grecaptcha.execute("your-site-key", { action: "submit" });
    try {
      setDisplayLoading(true);

      const response = await fetch('/api/prelaunch/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prelaunchModel,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        alert('An error occured when submitting request');
        setDisplayLoading(false);
        throw new Error(error.message || 'Submission failed');
      }

      pushToDataLayer(prelaunchModel);

      const data = await response.json();

      setDisplayLoading(false);
      setDisplayThankyou(true);
    } catch (err: any) {
      console.error('Submission error:', err.message);
      alert('An error occured when submitting request');
      setDisplayLoading(false);
    }
  };

  const pushToDataLayer = async (prelaunchModel: any) => {
    const salesforceLeads = prelaunchModel.salesforceLeads.toLowerCase();
    const fallbackFormName = salesforceLeads.includes('ryi')
      ? 'register your interest'
      : salesforceLeads.includes('preorder')
      ? 'pre order'
      : '';
    const formName = prelaunchModel.gaFormName?.trim() || fallbackFormName;
    const rawModelName = prelaunchModel.modelName?.trim() || '';
    const isForester = rawModelName.toLowerCase().includes('forester');
    const isNewForester = rawModelName.toLowerCase().startsWith('new ');
    const modelName =
      isForester && !isNewForester ? `New ${rawModelName}` : rawModelName;

    const variant = prelaunchModel.interestedVariant?.trim();
    const modelInterest = variant ? `${modelName} ${variant}` : modelName;
    const emailHashed = await digestStringSHA256(prelaunchModel.email);
    const phoneHashed = !!prelaunchModel.phone
      ? await digestStringSHA256(prelaunchModel.phone)
      : '';

    dataLayerPushRYIForm(formName, modelInterest, {
      emailHashed,
      phoneHashed,
      postcode: prelaunchModel.postcode,
      preferredDealer: prelaunchModel.dealer,
      state: suburb?.state ?? '',
    });
  };

  useEffect(() => {
    const fetchModels = async () => {
      const models = await getAllModels();
      setVehicleModels(models);
      const modelMatchCode = models.find((m) => m.name === props.modelName);
      setMatchCode(modelMatchCode?.pimID ?? '');

      const variants = modelMatchCode?.variants;
      setVehicleVariants(variants);
    };

    fetchModels();
  }, []);

  const displayMatrix = () => {
    setShowModal(true);
  };
  const handleCloseModal = () => {
    setShowModal(false);
  };
  const handleSelectedFromMatrix = (stockItem: StockItem) => {
    setSelectedModel(stockItem);
    const stockItemComment = `${stockItem.displayName} | ${stockItem.exteriorColourDescription}`;
  };
  const handleInterestedChange = (e: string) => {
    setInterestedInPreorder(e);
  };
  const handleVariantChange = (e: string) => {
    setSelectedVariant(e);
  };

  const asyncLoadOptions = (
    value: string,
    callback: (options: Option[]) => void
  ) => {
    if (value.length <= 2) {
      callback([]);
      setDefaultOptions([]);
      return;
    }

    fetch(`/api/location?search=${value}`)
      .then((res) => res.json())
      .then((res) => {
        const options = res.map((location) => ({
          label: `${location.name}, ${location.stateAbbreviationName} ${location.postcode}`,
          value: location.postcode,
          name: location.name,
          state: location.state,
        })) as PostCodeOption[];

        callback(options);
        setDefaultOptions(options);
      });
  };

  const handleOptionChange = async (value: Option | null) => {
    if (!!value) {
      if (value.value !== suburb?.value) {
        setSelectedModel(undefined);
      }

      const postcodeOption = value as PostCodeOption;
      setSuburb(postcodeOption);
      setDisabledMatrix(false);
    }
  };

  const handlePostCodeKeyDown = (e: React.KeyboardEvent) => {
    const allowedKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'Tab',
    ];
    const isNumberKey = /^[0-9]$/.test(e.key);
    if (!isNumberKey && !allowedKeys.includes(e.key)) {
      e.preventDefault();
    }
  };

  const postcodeFieldControl = (
    field: ControllerRenderProps<FieldValues, 'postcode'>
  ) => {
    return (
      <Autocomplete
        {...field}
        label="Postcode and suburb"
        bordered
        value={field.value}
        ref={autocompleteRef}
        defaultOptions={defaultOptions}
        loadOptions={asyncLoadOptions}
        onKeyDown={handlePostCodeKeyDown}
        onChange={(selectedOption) => {
          field.onChange(selectedOption);
          handleOptionChange(selectedOption);
        }}
        className={`${styles.postcodecontrol} required ${
          errors.postcode ? styles.postcodeerror : ''
        }`}
        placeholder=""
        name="postcode"
        dropdownIndicator={() => <></>}
        displayNoOptions
      />
    );
  };

  return (
    <div id="register-your-interest" className="container">
      {showModal && (
        <StockViewer
          postcode={suburb?.value ?? ''}
          model={props.modelName}
          matchCode={matchCode}
          onClose={handleCloseModal}
          onSelectedModel={handleSelectedFromMatrix}
        />
      )}

      {displayThankyou ? (
        <RYITankyouMessage message={props.competitionSubmitSuccessText} />
      ) : (
        <div className="prelaunch">
          <div className="row text-center">
            <div className="col-xs-12 col-md-8 col-lg-8 col-md-offset-2 col-lg-offset-2">
              <h2 className="h1 font-boxx">{props.competitionTitle}</h2>
            </div>
          </div>

          <div className="row">
            <div className="col-xs-12 col-md-8 col-lg-6 col-md-offset-2 col-lg-offset-3">
              <p className="text-center">{props.competitionSubTitle}</p>
              {captchaError && (
                <div
                  className="validation-summary-errors"
                  data-valmsg-summary="true"
                >
                  <ul>
                    <li>Please verify that you are not a robot.</li>
                  </ul>
                </div>
              )}
              {displayLoading ? (
                <div id="stock-loader">
                  <Loading />
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit(onSubmit)}
                  className="prelaunchForm"
                >
                  <div className="row">
                    {props.displayFirstName && (
                      <div className="col-12 col-sm-6 mb-3">
                        <div className="formField">
                          <label className="fieldLabel">First name</label>
                          <input
                            {...register('firstName', {
                              required: true,
                              pattern: {
                                value: /^[a-zA-Z]+[ a-zA-Z-_]*$/,
                                message: 'Invalid first name',
                              },
                            })}
                            className={`required text ${
                              errors.firstName ? styles.inputFieldError : ''
                            }`}
                          />
                          {errors.firstName && (
                            <span className={styles.fieldError}>
                              {errors.firstName.message ||
                                'First name is required'}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    {props.displayLastName && (
                      <div className="col-12 col-sm-6 mb-3">
                        <div className="formField">
                          <label className="fieldLabel">Last name</label>
                          <input
                            {...register('lastName', {
                              required: true,
                              pattern: {
                                value: /^[a-zA-Z]+[ a-zA-Z-_]*$/,
                                message: 'Invalid last name',
                              },
                            })}
                            className={`required text ${
                              errors.lastName ? styles.inputFieldError : ''
                            }`}
                          />
                          {errors.lastName && (
                            <span className={styles.fieldError}>
                              {errors.lastName.message ||
                                'Last name is required'}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="row">
                    {props.displayPhone && (
                      <div className="col-12 col-sm-6 mb-3">
                        <div className="formField">
                          <label className="fieldLabel">Phone</label>
                          <input
                            {...register('phone', {
                              required: 'Phone is required',
                              pattern: {
                                value: /^\d{10}$/,
                                message: 'Invalid phone number',
                              },
                            })}
                            className={`text ${
                              errors.phone ? styles.inputFieldError : ''
                            }`}
                            onInput={(e) => {
                              e.currentTarget.value =
                                e.currentTarget.value.replace(
                                  /[^0-9()+\s-]/g,
                                  ''
                                );
                            }}
                          />
                          {errors.phone && (
                            <span className={styles.fieldError}>
                              {errors.phone.message || 'Invalid phone number'}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    {props.displayEmail && (
                      <div className="col-12 col-sm-6 mb-3">
                        <div className="formField">
                          <label className="fieldLabel">Email</label>
                          <input
                            {...register('email', {
                              required: 'Email is required',
                              pattern: {
                                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                message: 'Enter a valid email address',
                              },
                            })}
                            className={`required text ${
                              errors.email ? styles.inputFieldError : ''
                            }`}
                            id="email"
                          />
                          {errors.email && (
                            <span className={styles.fieldError}>
                              {errors.email.message}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className={`row ${styles.rowArrangerFix}`}>
                    {props.displayDateOfBirth && (
                      <div className="col-12 col-sm-6 mb-3">
                        <div className="formField">
                          <label className="fieldLabel">Date of Birth</label>
                          <input
                            type="date"
                            {...register('dateOfBirth')}
                            className="text"
                            id="dateOfBirth"
                          />
                        </div>
                      </div>
                    )}

                    {props.displayModelTestDriven && (
                      <div className="col-12 col-sm-6 mb-3">
                        <div className="formField">
                          <label className="fieldLabel">Model</label>
                          <select
                            id="testDrivenModel"
                            className={`required text ${styles.selectMacBrowser}`}
                          >
                            {vehicleModels?.map((model) => (
                              <option key={model.name} value={model.name}>
                                {model.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    {props.displayPostCode && (
                      <div className="col-12 col-sm-6 mb-3">
                        <div className="formField">
                          <Controller
                            name="postcode"
                            control={control}
                            rules={{ required: 'Postcode is required' }}
                            render={({ field }) => postcodeFieldControl(field)}
                          />
                          {errors.postcode && (
                            <span className={styles.fieldError}>
                              Postcode is required
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {props.displayGender && (
                      <div className="col-12 col-sm-6 mb-3">
                        <div className="formField">
                          <Controller
                            name="gender"
                            control={control}
                            rules={{
                              required: 'Please choose an option',
                            }}
                            defaultValue=""
                            render={({ field }) => (
                              <>
                                <label htmlFor="gender" className="fieldLabel">
                                  Gender
                                </label>
                                <select
                                  id="gender"
                                  className={`required text  ${
                                    styles.selectMacBrowser
                                  } ${
                                    errors.gender ? styles.inputFieldError : ''
                                  }`}
                                  value={field.value}
                                  onChange={(e) =>
                                    field.onChange(e.target.value)
                                  }
                                >
                                  <option value="" disabled />
                                  <option value="Male">Male</option>
                                  <option value="Female">Female</option>
                                  <option value="Prefer not to say">
                                    Prefer not to say
                                  </option>
                                </select>
                                {errors.gender && (
                                  <span className={styles.fieldError}>
                                    {errors.gender.message}
                                  </span>
                                )}
                              </>
                            )}
                          />
                        </div>
                      </div>
                    )}

                    {props.displayRetailerCode && (
                      <div className="col-12 col-sm-6 mb-3">
                        <div className="formField">
                          <label className="fieldLabel">Retailer code</label>
                          <input
                            {...register('retailerCode', {
                              required: true,
                              pattern: {
                                value: /^[a-zA-Z0-9]{1,50}$/,
                                message:
                                  'Retailer code cannot exceed 50 characters.',
                              },
                            })}
                            className={`required text ${
                              errors.retailerCode ? styles.inputFieldError : ''
                            }`}
                          />
                          {errors.retailerCode && (
                            <span className={styles.fieldError}>
                              {errors.retailerCode.message ||
                                'Retailer code is required'}
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {props.displayDealerTextField && (
                      <div className="col-12 col-sm-6 mb-3">
                        <div className="formField">
                          <label className="fieldLabel">Dealer</label>
                          <input
                            {...register('dealer')}
                            className="text"
                            id="dealer"
                          />
                        </div>
                      </div>
                    )}

                    {props.displayPreOrderingInterestField && (
                      <div className="col-12 col-sm-6 mb-3">
                        <div className="formField">
                          <Controller
                            name="interestedInPreorder"
                            control={control}
                            rules={{
                              required: 'Please choose between Yes or No',
                            }}
                            defaultValue=""
                            render={({ field }) => (
                              <>
                                <label
                                  htmlFor="interestedInPreorder"
                                  className="fieldLabel"
                                >
                                  Interested in pre-ordering?
                                </label>
                                <select
                                  id="interestedInPreorder"
                                  className={`required text  ${
                                    styles.selectMacBrowser
                                  } ${
                                    errors.interestedInPreorder
                                      ? styles.inputFieldError
                                      : ''
                                  }`}
                                  value={field.value}
                                  onChange={(e) => {
                                    field.onChange(e.target.value); // update form state
                                    handleInterestedChange(e.target.value); // custom logic
                                  }}
                                >
                                  <option value="" disabled />
                                  <option value="Yes">Yes</option>
                                  <option value="No">No</option>
                                </select>
                                {errors.interestedInPreorder && (
                                  <span className={styles.fieldError}>
                                    {errors.interestedInPreorder.message}
                                  </span>
                                )}
                              </>
                            )}
                          />
                        </div>
                      </div>
                    )}

                    {props.displayStockAvailabilityMatrix && (
                      <div className="col-12 col-sm-6 mb-3">
                        <div className="formField">
                          <label
                            htmlFor="interestedInPreorder"
                            className="fieldLabel"
                          >
                            <span className={styles.inlineLabel}>
                              Preference
                              <TooltipDisclaimer disclaimer="Please enter your postcode to check available stock." />
                            </span>
                          </label>
                          <div
                            className={
                              disabledMatrix ? styles.disabledWrapper : ''
                            }
                          >
                            {selectedModel ? (
                              <>
                                <label className={styles.matrixLabel}>
                                  {selectedModel.displayName}{' '}
                                  {selectedModel.exteriorColourDescription}
                                  &nbsp;
                                  <a
                                    className={styles.matrixlink}
                                    onClick={displayMatrix}
                                  >
                                    Edit
                                  </a>
                                </label>
                              </>
                            ) : (
                              <>
                                <label className={styles.matrixLabel}>
                                  Want one ASAP? Check out some of our
                                  first&nbsp;
                                  <a
                                    className={styles.matrixlink}
                                    onClick={displayMatrix}
                                  >
                                    available vehicles
                                  </a>
                                </label>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {props.displayVariantType && (
                      <div className="col-12 col-sm-6 mb-3">
                        <div className="formField">
                          <Controller
                            name="interestedVariant"
                            control={control}
                            rules={{
                              required: 'Please choose an option',
                            }}
                            defaultValue=""
                            render={({ field }) => {
                              const getVariantOptions = () => {
                                const variantTypeLower =
                                  props.variantType?.toLowerCase() || '';

                                if (variantTypeLower === 'trailseeker') {
                                  return [
                                    'Pre-ordering an All-new Subaru Trailseeker',
                                    'Learning more about Subaru EVs',
                                    'Unsure at this stage',
                                  ];
                                } else if (variantTypeLower === 'uncharted') {
                                  return [
                                    'Pre-ordering an All-new Subaru Uncharted',
                                    'Learning more about Subaru EVs',
                                    'Unsure at this stage',
                                  ];
                                }
                                // Default options
                                return [
                                  'All-new Subaru Outback',
                                  'All-new Subaru Outback Wilderness',
                                  'Unsure at this stage',
                                ];
                              };

                              const variantOptions = getVariantOptions();

                              return (
                                <>
                                  <label
                                    htmlFor="interestedVariant"
                                    className="fieldLabel"
                                  >
                                    I&apos;m interested in:
                                  </label>
                                  <select
                                    id="interestedVariant"
                                    className={`required text  ${
                                      styles.selectMacBrowser
                                    } ${
                                      errors.interestedVariant
                                        ? styles.inputFieldError
                                        : ''
                                    }`}
                                    value={field.value}
                                    onChange={(e) => {
                                      field.onChange(e.target.value); // update form state
                                      handleVariantChange(e.target.value);
                                    }}
                                  >
                                    <option value="" disabled />
                                    {variantOptions.map((option) => (
                                      <option key={option} value={option}>
                                        {option}
                                      </option>
                                    ))}
                                  </select>
                                  {errors.interestedVariant && (
                                    <span className={styles.fieldError}>
                                      {errors.interestedVariant.message}
                                    </span>
                                  )}
                                </>
                              );
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {props.displayPrivacyCollectionStatement && (
                    <div className="formField checkbox">
                      <label
                        className="fieldLabel"
                        htmlFor="terms"
                        dangerouslySetInnerHTML={{
                          __html: props.privacyPolicy,
                        }}
                      />
                      <input
                        type="checkbox"
                        {...register('termsAndConditionsAccepted', {
                          required: true,
                        })}
                      />
                      {errors.termsAndConditionsAccepted && (
                        <span className={styles.fieldError}>
                          You must agree to the Privacy Collection Statement.
                        </span>
                      )}
                    </div>
                  )}
                  {/**
            <div className="row">
              <div className="col-xs-12">
                <div className="g-recaptcha" style={{ display: 'flex', justifyContent: 'center' }}>
                  <ReCAPTCHA sitekey={props.reCaptchaKey} onChange={handleCaptchaChange}  ref={recaptchaRef}/>
                </div>
              </div>
            </div>
 */}
                  <div className="row text-center">
                    <div className="col-xs-12">
                      <input
                        type="submit"
                        value="Submit"
                        className="btn submitButton"
                      />
                    </div>
                  </div>

                  <p>&nbsp;</p>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {props.displayPrivacyCollectionStatement && (
        <div className="formTerms">
          <label
            className="fieldLabel"
            htmlFor="terms"
            dangerouslySetInnerHTML={{ __html: props.competitionTerms }}
          />
        </div>
      )}
    </div>
  );
};
